import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

interface DeleteWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    isSoftDelete: boolean;
    isLoading?: boolean;
    warnings: string[];
}

export const DeleteWarningModal: React.FC<DeleteWarningModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    isSoftDelete,
    isLoading = false,
    warnings,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className={`p-6 text-center ${isSoftDelete ? 'bg-amber-50' : 'bg-red-50'}`}>
                    <div className="flex justify-center mb-4">
                        <div className={`p-3 rounded-full ${isSoftDelete ? 'bg-amber-100' : 'bg-red-100'}`}>
                            <AlertTriangle className={`w-8 h-8 ${isSoftDelete ? 'text-amber-600' : 'text-red-600'}`} />
                        </div>
                    </div>

                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                        {title}
                    </h2>

                    <div className="text-sm text-gray-600 mb-6 space-y-3">
                        {warnings.map((warning, index) => (
                            <p key={index} className="flex items-start justify-center gap-2">
                                <span className="mt-0.5">•</span>
                                <span className="text-left">{warning}</span>
                            </p>
                        ))}

                        <p className="font-medium text-gray-900 mt-4">
                            ¿Deseas continuar?
                        </p>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 flex justify-end gap-3 rounded-b-2xl border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                    >
                        Cancelar
                    </button>

                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:ring-2 disabled:opacity-50 flex items-center justify-center min-w-[120px] ${isSoftDelete
                            ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
                            : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                            }`}
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="flex items-center gap-2">
                                <Trash2 className="w-4 h-4" />
                                {isSoftDelete ? 'Desactivar' : 'Eliminar'}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
